/**
 * La sola parte della differenza che questo modulo produce.
 *
 * Dichiarata qui e non importata dai port perché **il dominio non dipende da nessun altro
 * pacchetto**: è la sua unica regola strutturale, quella che gli permette di girare identico dentro
 * un browser, una Tizen e una prova senza ambiente. Il tipo dei port è più largo — reminder,
 * avanzamento, impostazioni — e combacia con questo per struttura, che è tutto ciò che serve.
 */
export interface FavoritesPatch {
  readonly favorites: {
    readonly add?: ReadonlyArray<string>;
    readonly remove?: ReadonlyArray<string>;
  };
}

/**
 * I canali preferiti: l'elenco che l'utente tiene, e come si cambia.
 *
 * ## Si manda una differenza, mai l'elenco
 *
 * Oggi non si manda niente: i preferiti sono locali e questa funzione lavora su un elenco in memoria.
 * La regola resta scritta perché vale il giorno in cui la sincronia fra apparecchi si farà davvero, e
 * perché è il motivo per cui `toggleFavorite` restituisce una differenza e non l'elenco nuovo.
 *
 * *(Qui c'era il rimando a `PlayerPort.patchLineData`. Quel metodo **non esiste più**: è stato tolto
 * con i dati della linea, quando i promemoria — l'ultimo rimasto sul gateway — sono scesi in locale.
 * Un rimando a un simbolo che non c'è manda chi legge a cercarlo per dieci minuti.)*
 *
 * Il motivo per cui la differenza va applicata anche quando sembra più semplice fare altrimenti:
 * **gli apparecchi sono più d'uno**. Il televisore
 * in salotto e il telefono tengono ciascuno una copia dei preferiti, e quella copia invecchia appena
 * l'altro ne aggiunge uno. Chi rimanda l'elenco intero rimanda anche la propria ignoranza: cancella
 * il preferito che l'altro apparecchio ha appena aggiunto, e nessuno dei due se ne accorge finché
 * qualcuno non nota che un canale «è sparito dai preferiti da solo».
 *
 * Una differenza — «aggiungi questo», «togli quello» — non porta nessuna opinione su ciò che non
 * nomina, quindi due apparecchi che lavorano insieme non si cancellano a vicenda.
 *
 * ## L'elenco locale serve solo a disegnare
 *
 * `toggleFavorite` restituisce **entrambe** le cose: la differenza da mandare e l'elenco aggiornato
 * da mostrare subito. Il secondo è una previsione, non una verità: la verità torna dalla risposta
 * del gateway. Disegnare subito è quello che rende il tasto reattivo su un apparecchio lento;
 * crederci per sempre è quello che fa divergere due televisori.
 */

export interface FavoriteToggle {
  /** La differenza da mandare al gateway. Nomina **un** canale, mai l'elenco. */
  readonly patch: FavoritesPatch;
  /** L'elenco come sarà, per disegnarlo senza aspettare la risposta. */
  readonly optimistic: ReadonlyArray<string>;
  /** `true` se il canale è stato aggiunto, `false` se tolto. Serve all'avviso breve. */
  readonly added: boolean;
}

export function isFavorite(favorites: ReadonlyArray<string>, channelId: string): boolean {
  return favorites.includes(channelId);
}

export function toggleFavorite(
  favorites: ReadonlyArray<string>,
  channelId: string,
): FavoriteToggle {
  if (isFavorite(favorites, channelId)) {
    return {
      patch: { favorites: { remove: [channelId] } },
      optimistic: favorites.filter((id) => id !== channelId),
      added: false,
    };
  }

  /*
    In coda, non in testa.

    L'ordine dei preferiti è quello in cui sono stati aggiunti, ed è l'unico ordine che l'utente può
    prevedere: mettere l'ultimo in cima farebbe ballare la griglia sotto le dita di chi ne sta
    marcando cinque di fila, e la posizione che ci si ricorda cambierebbe a ogni aggiunta.
  */
  return {
    patch: { favorites: { add: [channelId] } },
    optimistic: [...favorites, channelId],
    added: true,
  };
}

/**
 * I canali preferiti nell'ordine del **catalogo**, non in quello di aggiunta.
 *
 * Due ordini diversi servono a due cose diverse, e confonderli si vede subito: l'ordine di aggiunta
 * conta per `line.app_data`, perché è un fatto e va conservato; ma una griglia di canali che li
 * mostra in quell'ordine è una griglia in cui «Rai 1» sta dopo un canale con numero 300, e nessuno
 * la sa leggere. Qui si riordina per disegnare, senza toccare il fatto.
 */
export function favoritesInCatalogOrder<T extends { readonly id: string }>(
  channels: ReadonlyArray<T>,
  favorites: ReadonlyArray<string>,
): ReadonlyArray<T> {
  const chosen = new Set(favorites);
  return channels.filter((channel) => chosen.has(channel.id));
}
